import { Component } from 'react';
import { withAlert } from 'react-alert';
import FontAwesome from 'react-fontawesome';
import { connect } from 'react-redux';
import classNames from 'classnames';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';

import { sendQuestSolution } from '@lootboy/lib/slices/cards';
import {
  isFutureAndTruthy,
  isPastAndTruthy,
} from '@lootboy/lib/utils/dateAndTime';

import Alert from '~/components/Alert';
import { OKAction } from '~/components/BootstrapDialog';
import ContentView from '~/components/ContentView';
import LightButton from '~/components/LightButton';
import LootBoyButton from '~/components/LootBoyButton';
import LootCountdown from '~/components/LootCountdown';

import { pushDialog } from '~/redux/actions/AlertActions';

import './style.css';

class MultiStageQuestView extends Component {
  static propTypes = {
    alert: PropTypes.object.isRequired,
    availableFrom: PropTypes.string,
    cardId: PropTypes.string.isRequired,
    expiresAt: PropTypes.string,
    isUsernameRequired: PropTypes.bool,
    quest: PropTypes.object.isRequired,
    questProgress: PropTypes.object.isRequired,
    // from mapDispatchToProps
    pushDialog: PropTypes.func.isRequired,
    sendQuestSolution: PropTypes.func.isRequired,
  };

  static STATUS_MAPPING = {
    IN_PROGRESS: 'LootGemQuestView_in_progress',
    REJECTED: 'LootGemQuestView_status_solution_rejected',
    VERIFICATION_REQUIRED: 'LootGemQuestView_status_verification_in_progress',
    COMPLETED: 'LootGemQuestView_status_completed',
    CANCELLED: 'LootGemQuestView_status_cancelled',
    EXPIRED: 'LootGemQuestView_status_time_ended',
  };

  static MAX_IMAGE_SIZE = 5 * 1024 * 1024;

  constructor(props) {
    super(props);

    this.imageInputs = {};

    this.state = {
      username: this.props.questProgress.username || '',
      questSolutions: {},
      screenshots: {},
      isQuestSolutionInvalid: {},
      countdownEnd: false,
      beginningCountdownEnd: false,
      missingUsername: false,
      processing: false,
    };
  }

  fileUpload = (e, index) => {
    e.preventDefault();

    const file = e.target.files[0];

    if (!file) {
      return;
    }

    if (file.size > MultiStageQuestView.MAX_IMAGE_SIZE) {
      this.props.pushDialog({
        body: I18n.t('Api_CardController_screenshot_too_big'),
        actions: [OKAction()],
      });

      return;
    }

    const reader = new FileReader();
    reader.onload = (event) =>
      this.onSolutionChanged(file, index, event.target.result);
    reader.readAsDataURL(file);
  };

  onUsernameChanged = (event) => {
    const username = event.target.value;
    this.setState({
      username,
      missingUsername: !username.trim(),
    });
  };

  onSubmitButtonPressed = () => {
    if (this.state.processing) return;
    this.setState({ processing: true });
    this.onSubmit().finally(() => this.setState({ processing: false }));
  };

  onSolutionChanged = (value, ndx, screenshot) => {
    const questSolutions = { ...this.state.questSolutions };
    const isQuestSolutionInvalid = { ...this.state.isQuestSolutionInvalid };
    const screenshots = { ...this.state.screenshots };
    questSolutions[ndx] = value;
    isQuestSolutionInvalid[ndx] = !screenshot && !value.trim();
    if (screenshot) {
      screenshots[ndx] = screenshot;
    }
    this.setState({
      questSolutions,
      isQuestSolutionInvalid,
      screenshots,
    });
  };

  onRemoveScreenshot = (ndx) => {
    const questSolutions = { ...this.state.questSolutions };
    const screenshots = { ...this.state.screenshots };
    const isQuestSolutionInvalid = { ...this.state.isQuestSolutionInvalid };
    questSolutions[ndx] = null;
    screenshots[ndx] = null;
    isQuestSolutionInvalid[ndx] = true;
    this.setState({
      questSolutions,
      screenshots,
      isQuestSolutionInvalid,
    });
  };

  onSubmit = async () => {
    const {
      cardId,
      isUsernameRequired,
      quest: { solutions },
      alert,
    } = this.props;

    const { questSolutions, username } = this.state;

    const isQuestUsernameValid =
      !isUsernameRequired || username.trim().length > 0;

    if (!isQuestUsernameValid) {
      this.setState({ missingUsername: true });
    }

    const isQuestSolutionInvalid = (solutions || []).reduce(
      (acc, solution, index) => {
        if (solution.solutionType === 'TEXT') {
          return !questSolutions[index] || !questSolutions[index].trim();
        }

        if (solution.solutionType === 'IMAGE') {
          return !questSolutions[index];
        }

        return false;
      },
      {},
    );

    const areQuestSolutionsInvalid = Object.keys(isQuestSolutionInvalid).some(
      (key) => isQuestSolutionInvalid[key],
    );

    if (areQuestSolutionsInvalid) {
      this.setState({ isQuestSolutionInvalid });
    }

    if (!isQuestUsernameValid || areQuestSolutionsInvalid) {
      this.props.pushDialog({
        body: I18n.t('ContactView_missing_data'),
        actions: [OKAction()],
      });

      return null;
    }

    const areScreenshotSizesValid = solutions.reduce(
      (acc, solution, index) =>
        acc &&
        (solution.solutionType !== 'IMAGE' ||
          !questSolutions[index] ||
          (questSolutions[index].size &&
            questSolutions[index].size < MultiStageQuestView.MAX_IMAGE_SIZE)),
      true,
    );

    if (!areScreenshotSizesValid) {
      this.props.pushDialog({
        body: I18n.t('Api_CardController_screenshot_too_big'),
        actions: [OKAction()],
      });

      return null;
    }

    const solutionsToUpload = solutions.map((solution, index) => ({
      solutionType: solution.solutionType,
      value: solution.solutionType === 'TEXT' ? questSolutions[index] : null,
    }));
    const data = {
      username,
      questApi: '2.0',
      solutions: solutionsToUpload,
    };

    const files = solutions
      .map((solution, index) => ({
        solutionType: solution.solutionType,
        value: questSolutions[index],
        index,
      }))
      .filter((solution) => solution.solutionType === 'IMAGE');

    return this.props
      .sendQuestSolution(cardId, data, files)
      .then(() =>
        alert.success(
          <Alert
            body={I18n.t('ContactView_successful_submit')}
            title={I18n.t('common_success')}
          />,
        ),
      )
      .catch((err) =>
        this.props.pushDialog({
          title: I18n.t('common_error'),
          body: err.message,
          actions: [OKAction()],
        }),
      );
  };

  renderCountdown = () => {
    const { expiresAt } = this.props;

    if (isPastAndTruthy(expiresAt)) {
      return (
        <div className="countdownContainer">
          <h4>{I18n.t('LootGemQuestView_times_up')}</h4>
        </div>
      );
    }

    return (
      <div className="countdownContainer">
        <h4>{I18n.t('LootGemQuestView_remaining_time')}</h4>
        <LootCountdown
          onEnd={() => this.setState({ countdownEnd: true })}
          value={expiresAt}
        />
      </div>
    );
  };

  renderBeginningTimer = () => {
    const { availableFrom } = this.props;

    return (
      <div className="countdownContainer">
        <h4>{I18n.t('LootGemQuestView_remaining_time_to_start')}</h4>
        <LootCountdown
          onEnd={() => this.setState({ beginningCountdownEnd: true })}
          value={availableFrom}
        />
      </div>
    );
  };

  renderUsernameInput(disabled) {
    const { isUsernameRequired } = this.props;

    return (
      isUsernameRequired && (
        <div>
          <div
            className={classNames({
              inputField: true,
              missingValue: !disabled && this.state.missingUsername,
            })}
          >
            {disabled && <p>{I18n.t('LootGemQuestView_username_label')}</p>}
            <FontAwesome
              name="user"
              size="2x"
            />
            <input
              disabled={disabled}
              onChange={this.onUsernameChanged}
              placeholder={I18n.t('LootGemQuestView_username_label')}
              type="text"
              value={this.state.username || ''}
            />
            {!disabled && this.state.missingUsername && (
              <FontAwesome
                className="missingValueIcon"
                name="exclamation-circle"
              />
            )}
          </div>
        </div>
      )
    );
  }

  renderForm() {
    const {
      isUsernameRequired,
      quest: { solutions: requiredSolutions },
    } = this.props;

    const { isQuestSolutionInvalid, screenshots } = this.state;

    return (
      <div>
        <h3>
          {I18n.t('LootGemQuestView_upload_here')}
          {':'}
        </h3>
        {isUsernameRequired && this.renderUsernameInput()}
        <div>
          {requiredSolutions.map((solution, index) => {
            if (solution.solutionType === 'TEXT') {
              return (
                <div key={index}>
                  <div
                    className={classNames({
                      inputField: true,
                      missingValue: isQuestSolutionInvalid[index],
                    })}
                  >
                    <p>{solution.placeholder}</p>
                    <input
                      onChange={(event) =>
                        this.onSolutionChanged(event.target.value, index)
                      }
                      type="text"
                      value={this.state.questSolutions[index] || ''}
                    />
                    {isQuestSolutionInvalid[index] && (
                      <FontAwesome
                        className="missingValueIcon"
                        name="exclamation-circle"
                      />
                    )}
                  </div>
                </div>
              );
            }

            if (solution.solutionType === 'IMAGE') {
              const screenshot = screenshots[index];

              return (
                <div key={index}>
                  <input
                    accept=".png, .jpg, .jpeg .gif"
                    id="fileInput"
                    name="fileInput"
                    onChange={(e) => this.fileUpload(e, index)}
                    ref={(ref) => {
                      this.imageInputs[index] = ref;
                    }}
                    style={{ display: 'none' }}
                    type="file"
                  />
                  <div className="buttonContainer">
                    <LightButton
                      className={classNames({
                        missingValue: isQuestSolutionInvalid[index],
                      })}
                      onClick={() => this.imageInputs[index].click()}
                      title={I18n.t('LootGemQuestView_screenshot_label')}
                    />
                    {isQuestSolutionInvalid[index] && (
                      <FontAwesome
                        className="missingValueIcon"
                        name="exclamation-circle"
                      />
                    )}
                  </div>
                  {screenshot && (
                    <div className="screenshot">
                      <img
                        alt=""
                        src={screenshot}
                      />
                      <FontAwesome
                        className="trashIcon"
                        name="times-circle"
                        onClick={() => this.onRemoveScreenshot(index)}
                        size="2x"
                      />
                    </div>
                  )}
                </div>
              );
            }

            return null;
          })}
        </div>

        <LootBoyButton
          className="submitButton"
          onClick={() => this.onSubmitButtonPressed()}
          title={I18n.t('ContactView_submit_button')}
        />
      </div>
    );
  }

  renderBeforeStartContent = () => {
    const {
      quest: { beforeStartContent },
    } = this.props;
    return (
      <div style={{ margin: 16 }}>
        {beforeStartContent.map((content, index) => (
          <ContentView
            key={index}
            {...content}
          />
        ))}
        {this.renderBeginningTimer()}
      </div>
    );
  };

  // eslint-disable-next-line complexity
  render() {
    const {
      expiresAt,
      availableFrom,
      quest,
      questProgress: { reason, allowedResubmission, status },
    } = this.props;

    const questStatus =
      status === 'IN_PROGRESS' && isPastAndTruthy(expiresAt)
        ? 'EXPIRED'
        : status;

    let timerToRender = null;
    let questContent = null;

    if (isFutureAndTruthy(availableFrom)) {
      timerToRender = this.renderBeforeStartContent;
    } else {
      questContent = (
        <div className="questContentContainer">
          <h3>
            {I18n.t('LootGemQuestView_follow_steps')}
            {':'}
          </h3>
          {quest.content.map((content, index) => (
            <ContentView
              key={index}
              {...content}
            />
          ))}
        </div>
      );
      if (
        expiresAt &&
        (questStatus === 'IN_PROGRESS' ||
          (questStatus === 'REJECTED' && allowedResubmission) ||
          questStatus === 'VERIFICATION_REQUIRED')
      ) {
        timerToRender = this.renderCountdown;
      }
    }

    return (
      <div className="questProgressContainer">
        <h2>{quest.name}</h2>
        {timerToRender && timerToRender()}
        <h4>
          {I18n.t('LootGemQuestView_current_quest_status')}
          {': '}
          {I18n.t(MultiStageQuestView.STATUS_MAPPING[questStatus])}
        </h4>
        {questStatus === 'REJECTED' && reason && (
          <p>
            <strong>
              {I18n.t('LootGemQuestView_reason_label')}
              {':'}
            </strong>{' '}
            {reason}
          </p>
        )}
        {questContent}
        <div className="inputArea">
          {(questStatus === 'IN_PROGRESS' ||
            (questStatus === 'REJECTED' && allowedResubmission)) &&
          (!expiresAt || isFutureAndTruthy(expiresAt)) &&
          (!availableFrom || isPastAndTruthy(availableFrom))
            ? this.renderForm()
            : null}
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = {
  sendQuestSolution,
  pushDialog,
};

export default withAlert()(
  connect(null, mapDispatchToProps)(MultiStageQuestView),
);
